import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { leaderboardAPI, puzzleAPI } from '../api';
import { AllTimeEntry, PuzzleLeaderboard } from '../types';
import { getGuestId } from '../utils/identity';
import { formatDuration } from '../utils/format';
import {
  colors,
  fonts,
  radius,
  shadow,
  numeric,
  sectionLabel,
  focusRing,
} from '../theme';

const Container = styled.div`
  max-width: 620px;
  margin: 0 auto;
  padding: 36px 20px 20px;
`;

const Title = styled.h1`
  font-family: ${fonts.display};
  font-size: 30px;
  text-align: center;
  margin: 0 0 6px;
`;

const Subtitle = styled.p`
  text-align: center;
  color: ${colors.inkFaint};
  font-size: 14px;
  margin: 0 0 26px;
`;

const Tabs = styled.div`
  display: flex;
  gap: 24px;
  justify-content: center;
  border-bottom: 1px solid ${colors.rule};
  margin-bottom: 22px;
`;

const Tab = styled.button<{ $active: boolean }>`
  position: relative;
  background: none;
  border: none;
  padding: 0 2px 11px;
  font-size: 14px;
  font-weight: ${props => (props.$active ? 700 : 500)};
  color: ${props => (props.$active ? colors.ink : colors.inkFaint)};
  cursor: pointer;
  ${focusRing}

  &:hover {
    color: ${colors.ink};
  }

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    height: 2px;
    background: ${props => (props.$active ? colors.accent : 'transparent')};
  }
`;

const Board = styled.div`
  background: ${colors.card};
  border: 1px solid ${colors.rule};
  border-radius: ${radius.md};
  box-shadow: ${shadow.panel};
  overflow: hidden;
`;

const BoardHead = styled.div`
  display: grid;
  grid-template-columns: 44px 1fr auto;
  gap: 12px;
  padding: 10px 16px;
  border-bottom: 1px solid ${colors.rule};
  background: ${colors.cardMuted};
  ${sectionLabel}
`;

const AllTimeHead = styled(BoardHead)`
  grid-template-columns: 44px 1fr 58px 68px;
`;

const Row = styled.div<{ $you: boolean }>`
  display: grid;
  grid-template-columns: 44px 1fr auto;
  gap: 12px;
  align-items: baseline;
  padding: 11px 16px;
  border-bottom: 1px solid ${colors.rule};
  background: ${props => (props.$you ? colors.accentWash : 'transparent')};

  &:last-child {
    border-bottom: none;
  }
`;

const AllTimeRow = styled(Row)`
  grid-template-columns: 44px 1fr 58px 68px;
`;

const Rank = styled.span<{ $rank: number }>`
  ${numeric}
  font-size: 14px;
  font-weight: 700;
  color: ${props =>
    props.$rank === 1
      ? colors.gold
      : props.$rank === 2
      ? colors.silver
      : props.$rank === 3
      ? colors.bronze
      : colors.inkFaint};
`;

const Name = styled.span<{ $you: boolean }>`
  font-size: 15px;
  font-weight: ${props => (props.$you ? 700 : 500)};
  color: ${colors.ink};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const YouTag = styled.span`
  ${sectionLabel}
  color: ${colors.accent};
  margin-left: 8px;
`;

const Time = styled.span`
  ${numeric}
  font-size: 15px;
  font-weight: 600;
  color: ${colors.ink};
  text-align: right;
`;

const Count = styled.span`
  ${numeric}
  font-size: 14px;
  color: ${colors.inkSoft};
  text-align: right;
`;

const Empty = styled.div`
  padding: 44px 20px;
  text-align: center;
  color: ${colors.inkFaint};
  font-family: ${fonts.display};
  font-size: 16px;
`;

const Note = styled.p`
  margin: 16px 0 0;
  text-align: center;
  font-size: 13px;
  color: ${colors.inkFaint};
`;

const BackLink = styled.button`
  display: block;
  margin: 20px auto 0;
  background: none;
  border: none;
  color: ${colors.accent};
  font-size: 14px;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
  ${focusRing}

  &:hover {
    color: ${colors.accentHover};
  }
`;

type TabKey = 'puzzle' | 'allTime';

export const Leaderboard: React.FC = () => {
  const { puzzleId } = useParams<{ puzzleId: string }>();
  const navigate = useNavigate();

  const [tab, setTab] = useState<TabKey>('puzzle');
  const [board, setBoard] = useState<PuzzleLeaderboard | null>(null);
  const [allTime, setAllTime] = useState<AllTimeEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const guestId = getGuestId();

  const loadPuzzleBoard = useCallback(async () => {
    // An explicit id wins; otherwise show today's puzzle.
    let targetId = puzzleId ? parseInt(puzzleId, 10) : NaN;

    if (Number.isNaN(targetId)) {
      const daily = await puzzleAPI.getDailyPuzzle();
      if (!daily) {
        setBoard(null);
        return;
      }
      targetId = daily.id;
    }

    setBoard(await leaderboardAPI.getPuzzleLeaderboard(targetId, guestId));
  }, [puzzleId, guestId]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const [, allTimeRows] = await Promise.all([
          loadPuzzleBoard(),
          leaderboardAPI.getAllTimeLeaderboard(guestId),
        ]);
        if (!cancelled) setAllTime(allTimeRows);
      } catch (error) {
        console.error('Error loading leaderboard:', error);
        if (!cancelled) setBoard(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [loadPuzzleBoard, guestId]);

  if (loading) {
    return (
      <Container>
        <Empty>Counting up the times…</Empty>
      </Container>
    );
  }

  return (
    <Container>
      <Title>Leaderboard</Title>
      <Subtitle>
        {tab === 'puzzle'
          ? board?.puzzle_title ?? 'No puzzle selected'
          : 'Every solver, every puzzle'}
      </Subtitle>

      <Tabs>
        <Tab $active={tab === 'puzzle'} onClick={() => setTab('puzzle')}>
          This puzzle
        </Tab>
        <Tab $active={tab === 'allTime'} onClick={() => setTab('allTime')}>
          All time
        </Tab>
      </Tabs>

      {tab === 'puzzle' ? (
        <Board>
          <BoardHead>
            <span>#</span>
            <span>Solver</span>
            <span>Time</span>
          </BoardHead>
          {!board || board.entries.length === 0 ? (
            <Empty>Nobody has finished this one yet. Be first.</Empty>
          ) : (
            board.entries.map(entry => (
              <Row key={`${entry.rank}-${entry.name}`} $you={entry.is_you}>
                <Rank $rank={entry.rank}>{entry.rank}</Rank>
                <Name $you={entry.is_you}>
                  {entry.name}
                  {entry.is_you && <YouTag>you</YouTag>}
                </Name>
                <Time>{formatDuration(entry.completion_time)}</Time>
              </Row>
            ))
          )}
        </Board>
      ) : (
        <Board>
          <AllTimeHead>
            <span>#</span>
            <span>Solver</span>
            <span style={{ textAlign: 'right' }}>Solved</span>
            <span style={{ textAlign: 'right' }}>Best</span>
          </AllTimeHead>
          {allTime.length === 0 ? (
            <Empty>No finished puzzles yet.</Empty>
          ) : (
            allTime.map(entry => (
              <AllTimeRow key={`${entry.rank}-${entry.name}`} $you={entry.is_you}>
                <Rank $rank={entry.rank}>{entry.rank}</Rank>
                <Name $you={entry.is_you}>
                  {entry.name}
                  {entry.is_you && <YouTag>you</YouTag>}
                </Name>
                <Count>{entry.solved}</Count>
                <Time>{formatDuration(entry.best_time)}</Time>
              </AllTimeRow>
            ))
          )}
        </Board>
      )}

      {tab === 'allTime' && allTime.length > 0 && (
        <Note>Ranked by puzzles solved, then by average time.</Note>
      )}

      {board && (
        <BackLink onClick={() => navigate(`/puzzle/${board.puzzle_id}`)}>
          Go to {board.puzzle_title}
        </BackLink>
      )}
    </Container>
  );
};

export default Leaderboard;
